/**
 * Preview Monitor — renders the currently selected project item
 * Updates live as properties change.
 * Supports drag-to-reposition: click and drag a title in the preview
 * to update its posX/posY transform values.
 */
import { monitorStore } from '../stores/monitorStore';
import { projectStore } from '../stores/projectStore';
import { uiStore } from '../stores/uiStore';
import { renderTemplate } from '../engine/templateRenderer';
import { eventBus } from '../engine/eventBus';

const bgClasses = [
  'checkerboard', 'preview-bg-black', 'preview-bg-grey', 'preview-bg-white',
  'preview-bg-program', 'preview-bg-shot-layout', 'preview-bg-image',
] as const;
const bgMap: Record<string, string> = {
  'alpha': 'checkerboard',
  'black': 'preview-bg-black',
  'grey': 'preview-bg-grey',
  'white': 'preview-bg-white',
  'program-monitor': 'preview-bg-program',
  'shot-layout': 'preview-bg-shot-layout',
  'image': 'preview-bg-image',
};

let canvasEl: HTMLElement;
let isDragging = false;

export function initPreview(): void {
  canvasEl = document.getElementById('preview-canvas')!;

  // Render when preview item changes
  monitorStore.subscribe((state, prevState) => {
    if (state.previewItemId !== prevState.previewItemId) {
      renderPreview();
    }
  });

  // Re-render when fields change (skip during drag — we handle position live)
  eventBus.on('properties:fieldChanged', () => { if (!isDragging) renderPreview(); });
  eventBus.on('project:itemUpdated', () => { if (!isDragging) renderPreview(); });

  // Re-render on window resize (for proper scaling)
  window.addEventListener('resize', () => {
    if (monitorStore.getState().previewItemId) {
      renderPreview();
    }
  });

  // Update background when preview background setting changes
  uiStore.subscribe((state, prevState) => {
    if (state.previewBackground !== prevState.previewBackground ||
        state.previewBackgroundImage !== prevState.previewBackgroundImage) {
      applyPreviewBackground();
    }
  });

  // Apply initial background
  applyPreviewBackground();

  // Re-render when video format changes (aspect ratio change)
  eventBus.on('zone:presetChanged', () => {
    if (monitorStore.getState().previewItemId) {
      // Delay one frame so aspect-ratio has been applied by program.ts
      requestAnimationFrame(() => renderPreview());
    }
  });

  // Drag-to-reposition
  initPreviewDrag();
}

function applyPreviewBackground(): void {
  if (!canvasEl) return;
  const { previewBackground: bg, previewBackgroundImage } = uiStore.getState();
  bgClasses.forEach((cls) => canvasEl.classList.remove(cls));
  // Clear any inline background-image
  canvasEl.style.backgroundImage = '';
  const cls = bgMap[bg];
  if (cls) canvasEl.classList.add(cls);
  // For custom image, set inline background-image
  if (bg === 'image' && previewBackgroundImage) {
    canvasEl.style.backgroundImage = `url('${previewBackgroundImage}')`;
  }
}

function renderPreview(): void {
  const itemId = monitorStore.getState().previewItemId;
  if (!itemId) {
    console.debug('[Preview] No preview item — clearing');
    canvasEl.innerHTML = '';
    applyPreviewBackground();
    return;
  }

  const item = projectStore.getState().getItem(itemId);
  if (!item) {
    console.debug('[Preview] Item not found:', itemId, '— clearing');
    canvasEl.innerHTML = '';
    applyPreviewBackground();
    return;
  }

  // Decklink cameras play live in Preview; regular videos show paused first frame
  const isLiveCamera = item.mediaType === 'video' && item.name.startsWith('Decklink');
  const usePreviewMode = !isLiveCamera;
  console.debug('[Preview] Rendering:', item.name, 'mediaType=', item.mediaType, 'preview=', usePreviewMode);

  // Remove background classes when showing content.
  bgClasses.forEach((cls) => canvasEl.classList.remove(cls));
  // Render into an inner wrapper so .template-viewport doesn't override
  // .preview-canvas sizing (aspect-ratio, height).
  canvasEl.innerHTML = '';
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:absolute;inset:0;';
  canvasEl.appendChild(wrapper);
  renderTemplate(wrapper, itemId, usePreviewMode);
}

// ── Drag-to-reposition ──────────────────────────────────────

function initPreviewDrag(): void {
  let startX = 0;
  let startY = 0;
  let startPosX = 0;
  let startPosY = 0;
  let dragItemId: string | null = null;

  canvasEl.style.cursor = 'default';

  canvasEl.addEventListener('mousedown', (e: MouseEvent) => {
    const itemId = monitorStore.getState().previewItemId;
    if (!itemId || e.button !== 0) return;
    const item = projectStore.getState().getItem(itemId);
    if (!item) return;

    isDragging = true;
    dragItemId = itemId;
    startX = e.clientX;
    startY = e.clientY;
    startPosX = item.posX ?? 0;
    startPosY = item.posY ?? 0;
    canvasEl.style.cursor = 'grabbing';
    e.preventDefault();
  });

  window.addEventListener('mousemove', (e: MouseEvent) => {
    if (!isDragging || !dragItemId) return;

    const rect = canvasEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    // Convert pixel delta to percentage of container
    const dx = ((e.clientX - startX) / rect.width) * 100;
    const dy = ((e.clientY - startY) / rect.height) * 100;

    const newPosX = Math.round((startPosX + dx) * 10) / 10;
    const newPosY = Math.round((startPosY + dy) * 10) / 10;

    // Update store (triggers program re-render but we skip preview re-render during drag)
    projectStore.getState().updateItem(dragItemId, { posX: newPosX, posY: newPosY });

    // Move the .template-inner directly for immediate visual feedback
    const inner = canvasEl.querySelector<HTMLElement>('.template-inner');
    if (inner) {
      const baseOffsetX = parseFloat(inner.dataset.baseLeft || inner.style.left) || 0;
      const baseOffsetY = parseFloat(inner.dataset.baseTop || inner.style.top) || 0;
      if (!inner.dataset.baseLeft) {
        // Store the centered offset (without user position) on first drag frame
        inner.dataset.baseLeft = String(baseOffsetX - (startPosX / 100) * rect.width);
        inner.dataset.baseTop = String(baseOffsetY - (startPosY / 100) * rect.height);
      }
      const bx = parseFloat(inner.dataset.baseLeft);
      const by = parseFloat(inner.dataset.baseTop);
      inner.style.left = `${bx + (newPosX / 100) * rect.width}px`;
      inner.style.top = `${by + (newPosY / 100) * rect.height}px`;
    }

    // Also move media items (no .template-inner)
    const mediaEl = canvasEl.querySelector<HTMLElement>('.template-viewport > img, .template-viewport > video, .template-viewport > div');
    if (mediaEl && !inner) {
      mediaEl.style.position = 'relative';
      mediaEl.style.left = `${newPosX}%`;
      mediaEl.style.top = `${newPosY}%`;
    }
  });

  window.addEventListener('mouseup', () => {
    if (!isDragging || !dragItemId) return;
    isDragging = false;
    canvasEl.style.cursor = 'default';
    // Emit field change so properties panel updates + program re-renders
    eventBus.emit('properties:fieldChanged', { itemId: dragItemId, fieldId: 'posX', value: '' });
    dragItemId = null;
    // Full re-render to clean up any direct DOM manipulation
    renderPreview();
  });
}
